import React, { useState } from 'react';
import { Calendar, Info, TrendingUp, Award, AlertCircle, Check, HelpCircle } from 'lucide-react';
import './LeaveConfigForm.css';

/**
 * Leave Configuration Form Component
 *
 * Allows HR to configure leave accrual rates and quotas for employees.
 * This component can be used in:
 * - New Employee creation
 * - Employee profile update
 * - Standalone leave configuration page
 */
const LeaveConfigForm = ({
  formData,
  setFormData,
  showTitle = true,
  compact = false
}) => {
  const [showHelper, setShowHelper] = useState(false);
  const [previewMonth, setPreviewMonth] = useState(6);

  // Default values
  const defaults = {
    slMonthlyAccrual: 1.0,
    clMonthlyAccrual: 1.0,
    elMonthlyAccrual: 1.25,
    slAnnualQuota: 12,
    clAnnualQuota: 12,
    elAnnualQuota: 15,
    isOnProbation: false,
    probationEndDate: '',
    noLeavesDuringProbation: false,
    accrualStartDate: ''
  };

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate preview balance for a given month
  const calculatePreview = (monthlyRate, quota, months) => {
    const accrued = months * parseFloat(monthlyRate || 0);
    return Math.min(accrued, parseFloat(quota || 0));
  };

  // Quick preset configurations
  const applyPreset = (presetType) => {
    const presets = {
      standard: {
        slMonthlyAccrual: 1.0,
        clMonthlyAccrual: 1.0,
        elMonthlyAccrual: 1.25,
        slAnnualQuota: 12,
        clAnnualQuota: 12,
        elAnnualQuota: 15
      },
      senior: {
        slMonthlyAccrual: 1.5,
        clMonthlyAccrual: 1.5,
        elMonthlyAccrual: 2.0,
        slAnnualQuota: 18,
        clAnnualQuota: 18,
        elAnnualQuota: 24
      },
      partTime: {
        slMonthlyAccrual: 0.5,
        clMonthlyAccrual: 0.5,
        elMonthlyAccrual: 0.625,
        slAnnualQuota: 6,
        clAnnualQuota: 6,
        elAnnualQuota: 7.5
      },
      contract: {
        slMonthlyAccrual: 0.5,
        clMonthlyAccrual: 0.5,
        elMonthlyAccrual: 0,
        slAnnualQuota: 6,
        clAnnualQuota: 6,
        elAnnualQuota: 0
      }
    };

    const preset = presets[presetType];
    if (preset) {
      Object.entries(preset).forEach(([key, value]) => {
        handleChange(key, value);
      });
    }
  };

  return (
    <div className={`leave-config-form ${compact ? 'compact' : ''}`}>
      {showTitle && (
        <div className="leave-config-header">
          <div className="leave-config-title-row">
            <Calendar className="leave-config-icon" size={24} />
            <div>
              <h2 className="leave-config-title">Leave Configuration</h2>
              <p className="leave-config-subtitle">
                Configure monthly accrual rates and annual leave quotas
              </p>
            </div>
          </div>
          <button
            type="button"
            className="help-toggle"
            onClick={() => setShowHelper(!showHelper)}
          >
            <HelpCircle size={18} />
            {showHelper ? 'Hide' : 'Show'} Help
          </button>
        </div>
      )}

      {showHelper && (
        <div className="leave-config-helper">
          <Info size={16} />
          <div>
            <p><strong>How it works:</strong></p>
            <ul>
              <li><strong>Monthly Accrual:</strong> Leaves earned per month (e.g., 1.0 = 1 leave/month)</li>
              <li><strong>Annual Quota:</strong> Maximum leaves allowed per year (cap)</li>
              <li><strong>Balance grows monthly:</strong> After 6 months with 1.0/month rate, employee has 6 leaves</li>
              <li><strong>Displayed as:</strong> Available/Quota (e.g., "6/12" means 6 available, 12 max)</li>
            </ul>
          </div>
        </div>
      )}

      {/* Quick Presets */}
      <div className="leave-config-section">
        <label className="section-label">
          <TrendingUp size={16} />
          Quick Presets
        </label>
        <div className="preset-buttons">
          <button
            type="button"
            className="preset-btn"
            onClick={() => applyPreset('standard')}
            title="1 SL, 1 CL, 1.25 EL per month"
          >
            Standard Employee
          </button>
          <button
            type="button"
            className="preset-btn"
            onClick={() => applyPreset('senior')}
            title="1.5 SL, 1.5 CL, 2 EL per month"
          >
            Senior Staff
          </button>
          <button
            type="button"
            className="preset-btn"
            onClick={() => applyPreset('partTime')}
            title="0.5 SL, 0.5 CL, 0.625 EL per month"
          >
            Part-Time
          </button>
          <button
            type="button"
            className="preset-btn"
            onClick={() => applyPreset('contract')}
            title="0.5 SL, 0.5 CL, no EL"
          >
            Contract Worker
          </button>
        </div>
      </div>

      {/* Sick Leave Configuration */}
      <div className="leave-config-section">
        <label className="section-label">
          <Award size={16} />
          Sick Leave (SL) Configuration
        </label>
        <div className="leave-type-config">
          <div className="config-row">
            <div className="form-group">
              <label>Monthly Accrual Rate</label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={formData.slMonthlyAccrual ?? defaults.slMonthlyAccrual}
                  onChange={(e) => handleChange('slMonthlyAccrual', parseFloat(e.target.value) || 0)}
                  placeholder="1.0"
                />
                <span className="input-suffix">per month</span>
              </div>
              <small>Sick leaves earned each month</small>
            </div>
            <div className="form-group">
              <label>Annual Quota (Cap)</label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={formData.slAnnualQuota ?? defaults.slAnnualQuota}
                  onChange={(e) => handleChange('slAnnualQuota', parseFloat(e.target.value) || 0)}
                  placeholder="12"
                />
                <span className="input-suffix">per year</span>
              </div>
              <small>Maximum SL allowed per year</small>
            </div>
          </div>
          <div className="preview-badge">
            <span className="preview-label">Preview (Month {previewMonth}):</span>
            <span className="preview-value">
              {calculatePreview(
                formData.slMonthlyAccrual ?? defaults.slMonthlyAccrual,
                formData.slAnnualQuota ?? defaults.slAnnualQuota,
                previewMonth
              )} / {formData.slAnnualQuota ?? defaults.slAnnualQuota}
            </span>
          </div>
        </div>
      </div>

      {/* Casual Leave Configuration */}
      <div className="leave-config-section">
        <label className="section-label">
          <Award size={16} />
          Casual Leave (CL) Configuration
        </label>
        <div className="leave-type-config">
          <div className="config-row">
            <div className="form-group">
              <label>Monthly Accrual Rate</label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={formData.clMonthlyAccrual ?? defaults.clMonthlyAccrual}
                  onChange={(e) => handleChange('clMonthlyAccrual', parseFloat(e.target.value) || 0)}
                  placeholder="1.0"
                />
                <span className="input-suffix">per month</span>
              </div>
              <small>Casual leaves earned each month</small>
            </div>
            <div className="form-group">
              <label>Annual Quota (Cap)</label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={formData.clAnnualQuota ?? defaults.clAnnualQuota}
                  onChange={(e) => handleChange('clAnnualQuota', parseFloat(e.target.value) || 0)}
                  placeholder="12"
                />
                <span className="input-suffix">per year</span>
              </div>
              <small>Maximum CL allowed per year</small>
            </div>
          </div>
          <div className="preview-badge">
            <span className="preview-label">Preview (Month {previewMonth}):</span>
            <span className="preview-value">
              {calculatePreview(
                formData.clMonthlyAccrual ?? defaults.clMonthlyAccrual,
                formData.clAnnualQuota ?? defaults.clAnnualQuota,
                previewMonth
              )} / {formData.clAnnualQuota ?? defaults.clAnnualQuota}
            </span>
          </div>
        </div>
      </div>

      {/* Earned Leave Configuration */}
      <div className="leave-config-section">
        <label className="section-label">
          <Award size={16} />
          Earned Leave (EL) Configuration
        </label>
        <div className="leave-type-config">
          <div className="config-row">
            <div className="form-group">
              <label>Monthly Accrual Rate</label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  max="10"
                  value={formData.elMonthlyAccrual ?? defaults.elMonthlyAccrual}
                  onChange={(e) => handleChange('elMonthlyAccrual', parseFloat(e.target.value) || 0)}
                  placeholder="1.25"
                />
                <span className="input-suffix">per month</span>
              </div>
              <small>Earned leaves earned each month</small>
            </div>
            <div className="form-group">
              <label>Annual Quota (Cap)</label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={formData.elAnnualQuota ?? defaults.elAnnualQuota}
                  onChange={(e) => handleChange('elAnnualQuota', parseFloat(e.target.value) || 0)}
                  placeholder="15"
                />
                <span className="input-suffix">per year</span>
              </div>
              <small>Maximum EL allowed per year</small>
            </div>
          </div>
          <div className="preview-badge">
            <span className="preview-label">Preview (Month {previewMonth}):</span>
            <span className="preview-value">
              {calculatePreview(
                formData.elMonthlyAccrual ?? defaults.elMonthlyAccrual,
                formData.elAnnualQuota ?? defaults.elAnnualQuota,
                previewMonth
              ).toFixed(2)} / {formData.elAnnualQuota ?? defaults.elAnnualQuota}
            </span>
          </div>
        </div>
      </div>

      {/* Preview Slider */}
      <div className="leave-config-section">
        <label className="section-label">
          <Info size={16} />
          Preview at Different Months
        </label>
        <div className="preview-slider-container">
          <input
            type="range"
            min="1"
            max="12"
            value={previewMonth}
            onChange={(e) => setPreviewMonth(parseInt(e.target.value))}
            className="preview-slider"
          />
          <div className="preview-slider-labels">
            <span>Month 1</span>
            <span className="preview-slider-value">Month {previewMonth}</span>
            <span>Month 12</span>
          </div>
        </div>
      </div>

      {/* Probation Settings */}
      <div className="leave-config-section">
        <label className="section-label">
          <AlertCircle size={16} />
          Probation Settings
        </label>
        <div className="probation-config">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="isOnProbation"
              checked={formData.isOnProbation ?? defaults.isOnProbation}
              onChange={(e) => handleChange('isOnProbation', e.target.checked)}
            />
            <label htmlFor="isOnProbation">Employee is on probation</label>
          </div>

          {formData.isOnProbation && (
            <>
              <div className="form-group">
                <label>Probation End Date</label>
                <input
                  type="date"
                  value={formData.probationEndDate ?? defaults.probationEndDate}
                  onChange={(e) => handleChange('probationEndDate', e.target.value)}
                />
                <small>Leave accrual starts after this date</small>
              </div>

              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="noLeavesDuringProbation"
                  checked={formData.noLeavesDuringProbation ?? defaults.noLeavesDuringProbation}
                  onChange={(e) => handleChange('noLeavesDuringProbation', e.target.checked)}
                />
                <label htmlFor="noLeavesDuringProbation">Block all leaves during probation</label>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="leave-config-section">
        <label className="section-label">
          <Calendar size={16} />
          Advanced Settings (Optional)
        </label>
        <div className="form-group">
          <label>Custom Accrual Start Date</label>
          <input
            type="date"
            value={formData.accrualStartDate ?? defaults.accrualStartDate}
            onChange={(e) => handleChange('accrualStartDate', e.target.value)}
          />
          <small>Override automatic calculation (leave empty to use joining date)</small>
        </div>
      </div>

      {/* Summary */}
      <div className="leave-config-summary">
        <div className="summary-header">
          <Check size={18} />
          <span>Configuration Summary</span>
        </div>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Sick Leave:</span>
            <span className="summary-value">
              {formData.slMonthlyAccrual ?? defaults.slMonthlyAccrual}/month,
              max {formData.slAnnualQuota ?? defaults.slAnnualQuota}/year
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Casual Leave:</span>
            <span className="summary-value">
              {formData.clMonthlyAccrual ?? defaults.clMonthlyAccrual}/month,
              max {formData.clAnnualQuota ?? defaults.clAnnualQuota}/year
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Earned Leave:</span>
            <span className="summary-value">
              {formData.elMonthlyAccrual ?? defaults.elMonthlyAccrual}/month,
              max {formData.elAnnualQuota ?? defaults.elAnnualQuota}/year
            </span>
          </div>
          {formData.isOnProbation && (
            <div className="summary-item warning">
              <AlertCircle size={14} />
              <span className="summary-label">Probation:</span>
              <span className="summary-value">
                {formData.noLeavesDuringProbation
                  ? `Blocked until ${formData.probationEndDate || 'end date'}`
                  : 'Active, leaves allowed'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveConfigForm;
